import { format } from 'date-fns'
import { ArrowUpRight, Download, Play } from 'lucide-react'
import { useState } from 'react'

import {
  AttachmentType,
  SupportRequest,
  SupportRequestAttachment,
} from '@/app/entities/SupportRequest'
import { cn } from '@/app/utils/cn'
import { handleDownload } from '@/app/utils/download'
import { formatMoneyValue } from '@/app/utils/formatMoneyValue'
import { StatusEnum, statusLabels } from '@/app/utils/status'

import { PROPOSAL_DETAILS_STEP } from '@/app/store/supportScreenContext'

import { resolveRequester } from '../resolveRequester'
import { resolveSimulation } from '../resolveSimulation'
import { InfoRow } from './InfoRow'
import { SupportSellerCard } from './SupportSellerCard'
import { withCdn } from '@/app/utils/withCdn'

interface SupportRequestContextProps {
  data: SupportRequest
  onOpenTransaction?: (transactionId: string) => void
  onOpenSeller?: (sellerId: string) => void
  productLabel?: string
}

// anexos da abertura do chamado + os que vieram nas respostas do vendedor
const collectAttachments = (data: SupportRequest) => {
  const fromHistory = (data.history ?? []).flatMap(
    (entry) => entry.attachments ?? [],
  )
  const all = [...(data.attachments ?? []), ...fromHistory]
  return all.filter(
    (attachment, index) =>
      all.findIndex((item) => item.url === attachment.url) === index,
  )
}

const formatDate = (value?: string) =>
  value ? format(new Date(value), 'dd/MM/yyyy · HH:mm') : undefined

const SectionTitle = ({ children }: { children: string }) => (
  <p className="pb-1 text-sm font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </p>
)

const SIMULATION_PREVIEW_ROWS = 5

type Simulation = NonNullable<ReturnType<typeof resolveSimulation>>

const SimulationBox = ({
  simulation,
  productLabel,
}: {
  simulation: Simulation
  productLabel?: string
}) => {
  const [expanded, setExpanded] = useState(false)

  const totalRows = simulation.sections.reduce(
    (sum, section) => sum + section.rows.length,
    0,
  )
  const canCollapse = totalRows > SIMULATION_PREVIEW_ROWS

  let remaining = expanded ? Infinity : SIMULATION_PREVIEW_ROWS
  const sections = simulation.sections
    .map((section) => {
      const rows = section.rows.slice(0, Math.max(remaining, 0))
      remaining -= rows.length
      return { ...section, rows }
    })
    .filter((section) => section.rows.length > 0)

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray01 p-4">
      <SectionTitle>Dados da simulação</SectionTitle>

      <div className="flex flex-col gap-1.5">
        <InfoRow label="Etapa" value={simulation.step} />
        <InfoRow label="Produto" value={productLabel} />
      </div>

      {sections.map((section) => (
        <div key={section.title}>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs font-medium text-black03">
              {section.title}
            </span>
            <div className="flex-1 border-t border-gray-300" />
          </div>

          <div className="flex flex-col gap-1.5">
            {section.rows.map((row) => (
              <InfoRow
                key={`${row.label}-${row.value}`}
                label={row.label}
                value={row.value}
              />
            ))}
          </div>
        </div>
      ))}

      {canCollapse && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="self-start text-sm text-primary hover:underline"
        >
          {expanded ? 'Mostrar menos' : `Mostrar tudo (${totalRows})`}
        </button>
      )}
    </div>
  )
}

export const SupportRequestContext = ({
  data,
  onOpenTransaction,
  onOpenSeller,
  productLabel,
}: SupportRequestContextProps) => {
  const attachments = collectAttachments(data)
  const isFromProposalDetails =
    data.metadata?.context?.step === PROPOSAL_DETAILS_STEP
  const simulation = isFromProposalDetails ? null : resolveSimulation(data)

  const openTransaction =
    !!data.transaction && onOpenTransaction
      ? () => onOpenTransaction(String(data.transaction))
      : undefined

  const requester = resolveRequester(data)

  return (
    <div className="flex flex-col gap-4 pt-2">
      {!!data.description && (
        <div className="flex flex-col gap-2 rounded-xl border border-gray01 p-4">
          <SectionTitle>Descrição</SectionTitle>
          <p className="whitespace-break-spaces text-sm text-gray-500">
            {data.description}
          </p>
        </div>
      )}

      {!!data.transaction && (
        <div
          tabIndex={openTransaction ? 0 : undefined}
          title={openTransaction ? 'Abrir transação' : undefined}
          onClick={openTransaction}
          onKeyDown={(event) => {
            if (event.key === 'Enter') openTransaction?.()
          }}
          className={cn(
            'relative flex flex-col gap-2 rounded-xl border border-gray01 p-4',
            openTransaction &&
              'cursor-pointer pr-10 transition-colors hover:border-black04 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          )}
        >
          {openTransaction && (
            <ArrowUpRight className="absolute right-3 top-3 h-4 w-4 text-black04" />
          )}
          <SectionTitle>Dados da proposta</SectionTitle>

          <div className="flex flex-col gap-1.5">
            <InfoRow label="Contrato" value={data.proposal} />
            <InfoRow label="Produto" value={productLabel} />
            <InfoRow
              label="Status"
              value={
                data.proposalStatus
                  ? (statusLabels[data.proposalStatus as StatusEnum] ??
                    data.proposalStatus)
                  : undefined
              }
            />
            <InfoRow
              label="Valor"
              value={
                data.proposalValue
                  ? formatMoneyValue(data.proposalValue)
                  : undefined
              }
            />
            <InfoRow
              label="Criada em"
              value={formatDate(data.proposalCreatedAt)}
            />
          </div>
        </div>
      )}

      {!!simulation && (
        <SimulationBox simulation={simulation} productLabel={productLabel} />
      )}

      {!!attachments.length && (
        <div className="flex flex-col gap-2 rounded-xl border border-gray01 p-4">
          <SectionTitle>{`Documentos (${attachments.length})`}</SectionTitle>
          <div className="flex flex-wrap gap-3">
            {attachments.map((attachment, index) => (
              <AttachmentPreview
                key={`${attachment.url}-${index}`}
                attachment={attachment}
              />
            ))}
          </div>
        </div>
      )}

      <SupportSellerCard
        sellerId={data.seller?.id}
        name={requester.name}
        phone={requester.phone}
        onOpenSeller={onOpenSeller}
        isUnauthenticated={requester.isUnauthenticated}
        isWhatsappDirect={requester.isWhatsappDirect}
        hasSeller={requester.hasSeller}
      />
    </div>
  )
}

const formatSize = (bytes?: number) => {
  if (!bytes) return null
  const mb = bytes / 1024 / 1024
  return mb >= 1
    ? `${mb.toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

const AttachmentPreview = ({
  attachment,
}: {
  attachment: SupportRequestAttachment
}) => {
  const isImage = attachment.type === AttachmentType.IMAGE
  const size = formatSize(attachment.size)

  return (
    <button
      type="button"
      title={`Baixar ${attachment.name}`}
      onClick={() =>
        handleDownload({ url: attachment.url, name: attachment.name })
      }
      className="group flex w-36 flex-col overflow-hidden rounded-lg border border-gray01 bg-white text-left transition-colors hover:border-black04"
    >
      <div className="relative h-24 w-full bg-gray02">
        {isImage ? (
          <img
            src={withCdn(attachment.url)}
            alt={attachment.name}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <>
            <video
              src={withCdn(attachment.url)}
              preload="metadata"
              className="h-full w-full object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex size-8 items-center justify-center rounded-full bg-black/60">
                <Play className="h-4 w-4 fill-white text-white" />
              </span>
            </span>
          </>
        )}

        <span className="absolute right-1 top-1 rounded-md bg-black/60 p-1 opacity-0 transition-opacity group-hover:opacity-100">
          <Download className="h-3 w-3 text-white" />
        </span>
      </div>

      <div className="flex flex-col gap-0.5 border-t border-gray01 p-2">
        <span className="truncate text-xs font-medium text-black03">
          {attachment.name}
        </span>
        <span className="text-[11px] text-black04">
          {isImage ? 'Imagem' : 'Vídeo'}
          {size ? ` · ${size}` : ''}
        </span>
      </div>
    </button>
  )
}
